import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { getProjectStatusValues, SearchProject } from "../../http/types/projects";
import { useProjectsApi } from "../../http/useProjects";
import { Paginated, PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import SvgEdit from "../icons/Edit";
import SvgVisibility from "../icons/Visibility";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import { getRemoveAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SearchBar from "../ui/SearchBar";
import StatusBadge from "../ui/StatusBadge";
import Table from "../ui/Table";

const SharedProjects: FC = () => {
  return (
    <SearchContextProvider>
      <SharedProjectsContent />
    </SearchContextProvider>
  );
};

const SharedProjectsContent: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const projectsApi = useProjectsApi();
  const { actions, setActions } = useSearchContext();

  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.projects.search;

  const stati = getProjectStatusValues(i18n.translation);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const [projects, setProjects] = useState<Paginated<SearchProject>>();
  const { data, error } = projectsApi.useSharedProjects(query, aborter.current.signal);
  const [projectToRemove, setProjectToRemove] = useState<SearchProject>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setProjects(data);
  }, [data?.items]);

  useEffect(() => {
    setActions(
      <SearchBar
        placeholder={searchPlaceholder}
        value={filters.state.q ?? ""}
        onChange={(term) => {
          aborter.current.abort();
          aborter.current = new AbortController();
          filters.setSearchTerm(term);
        }}
      />,
    );

    return () => {
      setActions(null);
    };
  }, [filters.state.q, searchPlaceholder, setActions]);

  return (
    <>
      <Header
        title={i18n.translation.projects.plural}
        error={error}
        path={{ display: i18n.translation.common.shared }}
        buttons={actions}
      />

      <FlexContent padding>
        <Table<SearchProject>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.common.status}</th>
              <th align="left">{i18n.translation.common.location}</th>
              <th align="left">{i18n.translation.common.country}</th>
              <th align="left">{i18n.translation.common.author}</th>
              <th align="left">{i18n.translation.common.lastModified}</th>
              <th align="center">{i18n.translation.common.canEdit}</th>
              <th></th>
            </tr>
          }
          items={projects?.items}
          renderRow={(item) => {
            return (
              <>
                <td>
                  <Link to={`/projects/${item.id}?tab=1`} onClick={(e) => e.stopPropagation()}>
                    {item.name}
                  </Link>
                </td>
                <td>
                  {(() => {
                    const status = stati.find((x) => x.value === item.status);
                    return status && <StatusBadge color={status.color}>{status.display}</StatusBadge>;
                  })()}
                </td>
                <td>{item.city}</td>
                <td>{item.country}</td>
                <td>
                  {item.author?.firstName} {item.author?.lastName}
                </td>
                <td>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</td>
                <td align="center">
                  <StatusBadge color={item.canEdit ? "success" : "error"}>{item.canEdit ? "✔" : "✘"}</StatusBadge>
                </td>
              </>
            );
          }}
          onRowClick={(item) => navigate(`/projects/${item.id}`)}
          rowActions={(item) => [
            {
              title: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
              onClick: () => navigate(`/projects/${item.id}`),
              icon: item.canEdit ? SvgEdit : SvgVisibility,
            },
            getRemoveAction(i18n, () => setProjectToRemove(item), true),
          ]}
          total={projects?.total}
          error={!!error}
        />
        {!!projectToRemove && (
          <ModalDialog
            onClose={() => setProjectToRemove(undefined)}
            title={i18n.translation.projects.removeShared}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                type: "secondary",
                onClick: () => setProjectToRemove(undefined),
              },
              {
                title: i18n.translation.common.remove,
                onClick: () => {
                  setIsSubmitting(true);
                  projectsApi
                    .removeSharedProject(projectToRemove.id)
                    .then(() => setProjectToRemove(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.projects.questions.removeShared}
          </ModalDialog>
        )}
      </FlexContent>
    </>
  );
};

export default SharedProjects;
